import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
    LayoutDashboard, 
    BookOpen, 
    Users, 
    UserCircle, 
    Tag,
    FileText,
    ChevronLeft,
    Menu
} from 'lucide-react';

export default function AdminLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, isAdmin } = useAuth();
    const [sidebarOpen, setSidebarOpen] = useState(true);

    useEffect(() => {
        if (!user || !isAdmin()) {
            navigate('/');
        }
    }, [user, isAdmin, navigate]);

    const navItems = [
        { path: '/admin', icon: LayoutDashboard, label: 'Přehled', exact: true },
        { path: '/admin/povidky', icon: BookOpen, label: 'Povídky' },
        { path: '/admin/autori', icon: UserCircle, label: 'Autoři' },
        { path: '/admin/uzivatele', icon: Users, label: 'Uživatelé' },
        { path: '/admin/promo-kody', icon: Tag, label: 'Promo kódy' },
        { path: '/admin/audit-log', icon: FileText, label: 'Audit log' },
    ];

    const isActive = (path, exact = false) => {
        if (exact) {
            return location.pathname === path;
        }
        return location.pathname.startsWith(path);
    };

    if (!user || !isAdmin()) {
        return null;
    }

    return (
        <div className="min-h-screen flex" data-testid="admin-layout">
            {/* Sidebar */}
            <aside 
                className={`${sidebarOpen ? 'w-64' : 'w-16'} bg-card border-r border-border min-h-screen transition-all duration-300`}
            >
                {/* Sidebar Header */}
                <div className="h-16 flex items-center justify-between px-4 border-b border-border">
                    {sidebarOpen && (
                        <Link to="/" className="text-xl italic">
                            Listovna
                        </Link>
                    )}
                    <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        className="p-2 hover:bg-secondary rounded-lg transition-colors"
                        aria-label={sidebarOpen ? 'Skrýt menu' : 'Zobrazit menu'}
                    >
                        {sidebarOpen ? (
                            <ChevronLeft className="w-5 h-5" />
                        ) : (
                            <Menu className="w-5 h-5" />
                        )}
                    </button>
                </div>

                {/* Navigation */}
                <nav className="p-2">
                    {navItems.map((item) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`admin-sidebar-link rounded-lg mb-1 ${
                                isActive(item.path, item.exact) ? 'active' : ''
                            }`}
                            data-testid={`admin-nav-${item.path.split('/').pop() || 'dashboard'}`}
                        >
                            <item.icon className="w-5 h-5 flex-shrink-0" />
                            {sidebarOpen && <span>{item.label}</span>}
                        </Link>
                    ))}
                </nav>

                {/* Back to site */}
                <div className="absolute bottom-4 left-0 right-0 px-2">
                    <Link
                        to="/"
                        className="admin-sidebar-link rounded-lg"
                        data-testid="admin-back-to-site"
                    >
                        <ChevronLeft className="w-5 h-5 flex-shrink-0" />
                        {sidebarOpen && <span>Zpět na web</span>}
                    </Link>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 overflow-auto">
                <div className="p-6 md:p-8">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
